/**
 * Context manager for observability.
 *
 * Manages the canonical context lifecycle:
 *  - setContext: on login
 *  - updateContext: on org/persona switch, or when the profile settles
 *  - resetContext: on logout
 *
 * Both PostHog and Sentry receive context updates simultaneously.
 *
 * Tenant identity (W0.3 · F14): `tenantId` is the user's `user_tenants` row
 * with `isDefault = true`, else any membership — resolved by
 * `resolveTenantId`. It is optional, and when it is absent the `tenant_id`
 * tag is OMITTED and the PostHog tenant group is SKIPPED rather than filled
 * with a stand-in. The sponsoring company is reported separately as
 * `sponsor_tenant_id` and never overwrites it.
 */

import type {
  ObservabilityContext,
  ObservabilityContextProperties,
} from './observability-context.types';
import type { IAnalyticsClient } from '../analytics/analytics-client.types';
import type { IErrorClient } from '../errors/error-client.types';
import { POSTHOG_TENANT_GROUP } from '../constants';

export interface IObservabilityContextManager {
  /** Set full context on login */
  setContext(context: ObservabilityContext): void;
  /** Update partial context on org/persona switch */
  updateContext(partial: Partial<ObservabilityContext>): void;
  /** Clear user context on logout */
  resetContext(): void;
  /** Get current context snapshot */
  getContext(): ObservabilityContext | null;
}

export class ObservabilityContextManager implements IObservabilityContextManager {
  private context: ObservabilityContext | null = null;

  constructor(
    private readonly analytics: IAnalyticsClient,
    private readonly errors: IErrorClient
  ) {}

  setContext(context: ObservabilityContext): void {
    this.context = { ...context };

    // PostHog: identify + person properties. `tenant_id` / `sponsor_tenant_id`
    // are spread in only when known — an absent key is the honest report.
    this.analytics.identify(context.userId, {
      ...(context.tenantId ? { tenant_id: context.tenantId } : {}),
      ...(context.sponsorTenantId ? { sponsor_tenant_id: context.sponsorTenantId } : {}),
      persona: context.persona,
      portal: context.portal,
      env: context.env,
      release: context.release,
    });

    // PostHog: group by tenant — skipped entirely without a tenant, since a
    // group call needs a real group id and there is nothing to invent.
    if (context.tenantId) {
      this.analytics.group(POSTHOG_TENANT_GROUP, context.tenantId, {
        portal: context.portal,
      });
    }

    // Sentry: user + tags
    this.errors.setUser({
      id: context.userId,
      ...(context.tenantId ? { tenantId: context.tenantId } : {}),
      persona: context.persona,
    });
    this.errors.setTags({
      portal: context.portal,
      env: context.env,
      release: context.release,
      ...(context.tenantId ? { tenant_id: context.tenantId } : {}),
      ...(context.sponsorTenantId ? { sponsor_tenant_id: context.sponsorTenantId } : {}),
      persona: context.persona,
    });
  }

  updateContext(partial: Partial<ObservabilityContext>): void {
    if (!this.context) {
      return;
    }

    this.context = { ...this.context, ...partial };

    // The tenant arrives late on every portal: the profile / portals query
    // settles after login, so this is the normal path, not an edge case.
    if (partial.tenantId) {
      this.analytics.group(POSTHOG_TENANT_GROUP, partial.tenantId);
      this.errors.setTag('tenant_id', partial.tenantId);
    }

    // The sponsor is its own relationship — it never touches `tenant_id`.
    if (partial.sponsorTenantId) {
      this.errors.setTag('sponsor_tenant_id', partial.sponsorTenantId);
    }

    if (partial.persona) {
      this.analytics.setPersonProperty('persona', partial.persona);
      this.errors.setTag('persona', partial.persona);
    }
  }

  resetContext(): void {
    this.context = null;
    this.analytics.reset();
    this.errors.clearUser();
  }

  getContext(): ObservabilityContext | null {
    return this.context ? { ...this.context } : null;
  }

  /**
   * Returns context as snake_case properties for PostHog events.
   * Called internally by the analytics client to attach standard properties.
   */
  getContextProperties(): ObservabilityContextProperties | null {
    if (!this.context) {
      return null;
    }

    return {
      user_id: this.context.userId,
      ...(this.context.tenantId ? { tenant_id: this.context.tenantId } : {}),
      ...(this.context.sponsorTenantId ? { sponsor_tenant_id: this.context.sponsorTenantId } : {}),
      persona: this.context.persona,
      portal: this.context.portal,
      env: this.context.env,
      release: this.context.release,
      trace_id: this.context.traceId,
    };
  }
}
